/**
 * Homepage domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client.
 *
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `HomepageMutationId` registry in `./invalidation`.
 */

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListHomepagePins,
  listHomepagePinsQueryKey,
  useListHomepageRecents,
  listHomepageRecentsQueryKey,
} from '../generated/hooks/homepageController'

// ── Generated query/mutation parameter & response type re-exports ──

export type { ListHomepagePinsQueryResponse } from '../generated/types/ListHomepagePins'
export type { ListHomepageRecentsQueryResponse } from '../generated/types/ListHomepageRecents'
export type {
  CreateHomepagePinMutationRequest,
  CreateHomepagePinMutationResponse,
} from '../generated/types/CreateHomepagePin'
export type { DeleteHomepagePinPathParams } from '../generated/types/DeleteHomepagePin'
export type {
  ReorderHomepagePinsMutationRequest,
  ReorderHomepagePinsMutationResponse,
} from '../generated/types/ReorderHomepagePins'
export type {
  RecordHomepageVisitMutationRequest,
  RecordHomepageVisitMutationResponse,
} from '../generated/types/RecordHomepageVisit'

// ── Internal imports for mutation wrappers ─────────────────────────

import { createHomepagePin } from '../generated/clients/createHomepagePin'
import { deleteHomepagePin } from '../generated/clients/deleteHomepagePin'
import { reorderHomepagePins } from '../generated/clients/reorderHomepagePins'
import { recordHomepageVisit } from '../generated/clients/recordHomepageVisit'

import type { CreateHomepagePinMutationRequest } from '../generated/types/CreateHomepagePin'
import type { DeleteHomepagePinPathParams } from '../generated/types/DeleteHomepagePin'
import type { ReorderHomepagePinsMutationRequest } from '../generated/types/ReorderHomepagePins'
import type { RecordHomepageVisitMutationRequest } from '../generated/types/RecordHomepageVisit'

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useCreateHomepagePin() {
  return useInvalidatingMutation('createHomepagePin', invalidationRegistry, {
    mutationFn: (variables: { data: CreateHomepagePinMutationRequest }) =>
      createHomepagePin(variables.data),
  })
}

export function useDeleteHomepagePin() {
  return useInvalidatingMutation('deleteHomepagePin', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteHomepagePinPathParams['id'] }) =>
      deleteHomepagePin({ id: variables.id }),
  })
}

export function useReorderHomepagePins() {
  return useInvalidatingMutation('reorderHomepagePins', invalidationRegistry, {
    mutationFn: (variables: { data: ReorderHomepagePinsMutationRequest }) =>
      reorderHomepagePins(variables.data),
  })
}

export function useRecordHomepageVisit() {
  return useInvalidatingMutation('recordHomepageVisit', invalidationRegistry, {
    mutationFn: (variables: { data: RecordHomepageVisitMutationRequest }) =>
      recordHomepageVisit(variables.data),
  })
}
